import { useEffect, useState } from 'react';
import { AlertTriangle, CreditCard, Landmark, Mail, QrCode, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useCarga } from '@/features/dynamic-pricing/monitoreo/hooks/use-carga';
import {
  atributosSoporteNoPuede,
  AVISO_SOPORTE_NO_PUEDE,
  useEnSoporte,
} from '@/features/mini-prixia/soporte-solo-mira';
import { toast } from '@/lib/hooks/use-toast';
import { cn } from '@/lib/utils/cn';
import { formatoPrecioEn } from '@/lib/utils/formato-precio';
import { MarcoDeMiEmpresa } from '../components/marco-de-mi-empresa';
import {
  miEmpresaService,
  type Facturacion,
  type PedidoDeFacturacion,
} from '../services/mi-empresa-service';
import { useEmpresaStore, type DatosDeFacturacion } from '../store/empresa-store';

const CORREO = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const fechaLarga = (iso: string) =>
  new Date(iso).toLocaleDateString('es-BO', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });

/** Lo que dice cada diálogo de «pedir»: Prixia no guarda tarjetas ni inventa una cuenta. */
const PEDIDOS: Record<PedidoDeFacturacion, { titulo: string; texto: string; boton: string }> = {
  CAMBIAR_TARJETA: {
    titulo: 'Cambiar la tarjeta',
    texto:
      'Por seguridad, Prixia no guarda los datos de tu tarjeta: el cambio lo hace nuestro equipo de cobranzas con el banco. Te escribimos al correo de facturación para hacerlo.',
    boton: 'Pedir el cambio',
  },
  AGREGAR_TARJETA: {
    titulo: 'Agregar una tarjeta',
    texto:
      'Por seguridad, Prixia no guarda los datos de tu tarjeta: el registro lo hace nuestro equipo de cobranzas con el banco. Te escribimos al correo de facturación para hacerlo.',
    boton: 'Pedir el registro',
  },
  QR: {
    titulo: 'Pagar con QR',
    texto:
      'Te mandamos el QR de la cuota de este mes al correo de facturación, listo para escanear desde la app de tu banco.',
    boton: 'Pedir el QR',
  },
  TRANSFERENCIA: {
    titulo: 'Pagar por transferencia',
    texto: 'Te mandamos los datos de la cuenta al correo de facturación.',
    boton: 'Pedir los datos',
  },
};

/** Mi empresa › Pagos y facturación (maqueta de Melanie, 25-oct). */
export function PagosSection() {
  const empresa = useEmpresaStore((s) => s.empresa);
  const guardarEmpresa = useEmpresaStore((s) => s.guardar);
  const facturacion = useCarga(() => miEmpresaService.facturacion(), []);
  const [pedido, setPedido] = useState<PedidoDeFacturacion | null>(null);
  const [debito, setDebito] = useState(false);
  const enSoporte = useEnSoporte();

  useEffect(
    () => setDebito(empresa?.billingInfo?.debitoAutomatico ?? false),
    [empresa?.billingInfo?.debitoAutomatico]
  );

  const cambiarDebito = async (v: boolean) => {
    setDebito(v);
    try {
      await guardarEmpresa({ billingInfo: { debitoAutomatico: v } });
      toast({
        title: v ? 'Débito automático encendido' : 'Débito automático apagado',
        description: v ? undefined : 'Desde el próximo mes pagas a mano, con QR o transferencia.',
      });
    } catch {
      setDebito(!v);
      toast({ title: 'No se pudo cambiar el débito automático', variant: 'destructive' });
    }
  };

  const f = facturacion.datos;

  return (
    <MarcoDeMiEmpresa seccion="pagos">
      {!empresa ? (
        <Skeleton className="h-[520px] w-full rounded-tarjeta" />
      ) : (
        <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)]">
          <section
            aria-labelledby="titulo-medio-de-pago"
            className="rounded-tarjeta border bg-card p-6 shadow-card"
          >
            <h2
              id="titulo-medio-de-pago"
              className="flex items-center gap-2.5 text-xl font-semibold"
            >
              Medio de pago
              <span className="rounded-full bg-degradado-prixia px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-primary-foreground">
                Recomendado
              </span>
            </h2>

            <div className="mt-5 grid gap-6 md:grid-cols-[260px_minmax(0,1fr)] 2xl:grid-cols-[300px_minmax(0,1fr)]">
              <TarjetaVisual titular={empresa.billingInfo?.razonSocial || empresa.name} />

              <div className="flex flex-col gap-3">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p id="titulo-debito" className="text-lg font-semibold">
                      Débito automático
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Cobramos tu plan a esta tarjeta cada mes.
                    </p>
                  </div>
                  <Switch
                    aria-labelledby="titulo-debito"
                    tamano="lg"
                    checked={debito}
                    onCheckedChange={cambiarDebito}
                    disabled={enSoporte}
                  />
                </div>
                <ProximoCobro facturacion={f} cargando={facturacion.estado === 'cargando'} />
                <div className="flex flex-col items-start gap-2">
                  <Button
                    variant="outline"
                    className="rounded-full"
                    onClick={() => setPedido('CAMBIAR_TARJETA')}
                    {...atributosSoporteNoPuede(enSoporte)}
                  >
                    <CreditCard className="size-4" aria-hidden="true" />
                    Cambiar tarjeta
                  </Button>
                  <Button
                    variant="link"
                    className="h-auto p-0"
                    onClick={() => setPedido('AGREGAR_TARJETA')}
                    {...atributosSoporteNoPuede(enSoporte)}
                  >
                    + Agregar otra tarjeta
                  </Button>
                </div>
              </div>
            </div>

            <hr className="my-6 border-border" />
            <p className="etiqueta-caps">
              Otros medios de pago{' '}
              <span className="normal-case tracking-normal text-muted-foreground">
                · pago manual cada mes
              </span>
            </p>
            <ul className="mt-3 flex flex-col gap-2.5">
              <MedioManual
                icono={QrCode}
                titulo="QR"
                detalle="escanéalo desde la app de tu banco"
                accion="Ver QR"
                pideAlServidor={!f?.qrUrl}
                onAccion={() =>
                  f?.qrUrl ? window.open(f.qrUrl, '_blank', 'noopener') : setPedido('QR')
                }
              />
              <MedioManual
                icono={Landmark}
                titulo="Transferencia bancaria"
                detalle={
                  f?.transferencia
                    ? `${f.transferencia.banco} · cuenta ${f.transferencia.cuenta} · a nombre de ${f.transferencia.titular}`
                    : 'te mandamos los datos de la cuenta por correo'
                }
                accion="Ver datos"
                pideAlServidor={!f?.transferencia}
                onAccion={() => setPedido('TRANSFERENCIA')}
                datos={f?.transferencia ?? null}
              />
            </ul>
          </section>

          <DatosDeFacturacionCard />
        </div>
      )}

      {pedido && <DialogoDePedido tipo={pedido} onCerrar={() => setPedido(null)} />}
    </MarcoDeMiEmpresa>
  );
}

/**
 * La tarjeta dibujada de la maqueta. Prixia no guarda los números de ninguna
 * tarjeta, así que se dibuja sin ellos: nunca muestra datos que no tiene.
 */
function TarjetaVisual({ titular }: { titular: string }) {
  return (
    <div
      role="img"
      aria-label="Tarjeta del débito automático (los números no se guardan en Prixia)"
      className="relative flex aspect-[1.6] w-full max-w-[300px] flex-col justify-between overflow-hidden rounded-2xl bg-[#2A2A2E] p-5 text-white shadow-xl" // pulido: permitido — el plástico de la tarjeta es oscuro en los dos temas
    >
      <div className="flex items-start justify-between">
        <span
          className="h-7 w-10 rounded-md bg-gradient-to-br from-[#E8C76A] to-[#B8913A]"
          aria-hidden="true"
        />
        <span className="text-xs font-semibold text-white/85">Débito automático</span>
      </div>
      <p className="whitespace-nowrap font-mono text-base tracking-[0.12em]">•••• •••• •••• ••••</p>
      <div className="flex items-end justify-between gap-3 text-[11px]">
        <div className="min-w-0">
          <p className="uppercase text-white/60">Titular</p>
          <p className="truncate font-medium">{titular}</p>
        </div>
        <div>
          <p className="uppercase text-white/60">Vence</p>
          <p className="font-medium">••/••</p>
        </div>
      </div>
    </div>
  );
}

function ProximoCobro({
  facturacion: f,
  cargando,
}: {
  facturacion: Facturacion | null;
  cargando: boolean;
}) {
  if (cargando && !f) return <Skeleton className="h-6 w-56" />;
  if (!f?.plan || !f.proximoCobro)
    return (
      <p className="text-sm text-muted-foreground">
        Tu empresa todavía no tiene un plan con cobro mensual.
      </p>
    );
  const pronto = f.diasParaVencer != null && f.diasParaVencer <= 7;
  return (
    <div className="flex flex-col items-start gap-2">
      <p className="text-[15px]">
        Próximo cobro: <b>{fechaLarga(f.proximoCobro)}</b> ·{' '}
        <b>
          {f.plan.moneda} {formatoPrecioEn(f.plan.precioMensual, 'en-US')}
        </b>
        <span className="text-muted-foreground"> · plan {f.plan.nombre}</span>
      </p>
      {pronto && (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-alerta-suave px-3 py-1 text-sm font-semibold text-alerta">
          <AlertTriangle className="size-4" aria-hidden="true" />
          {f.diasParaVencer === 0
            ? 'Tu pago vence hoy'
            : `Tu pago vence en ${f.diasParaVencer} ${f.diasParaVencer === 1 ? 'día' : 'días'}`}
        </span>
      )}
    </div>
  );
}

function MedioManual({
  icono: Icono,
  titulo,
  detalle,
  accion,
  onAccion,
  datos,
  pideAlServidor,
}: {
  icono: typeof QrCode;
  titulo: string;
  detalle: string;
  accion: string;
  onAccion: () => void;
  datos?: { banco: string; cuenta: string; titular: string } | null;
  /** Si el botón manda un pedido (POST) en vez de mostrar algo que ya está cargado. */
  pideAlServidor: boolean;
}) {
  const [viendo, setViendo] = useState(false);
  const enSoporte = useEnSoporte();
  return (
    <li className="flex items-center gap-4 rounded-2xl bg-muted/50 px-4 py-3">
      <span className="grid size-10 shrink-0 place-items-center rounded-xl border bg-card">
        <Icono className="size-5 text-muted-foreground" aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-semibold">{titulo}</span>
        <span className="block truncate text-sm text-muted-foreground">{detalle}</span>
      </span>
      <Button
        variant="link"
        className="h-auto p-0"
        onClick={() => (datos ? setViendo(true) : onAccion())}
        aria-label={`${accion}: ${titulo}`}
        {...atributosSoporteNoPuede(enSoporte && pideAlServidor)}
      >
        {accion}
      </Button>
      {datos && viendo && (
        <Dialog open onOpenChange={(a) => !a && setViendo(false)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Pagar por transferencia</DialogTitle>
              <DialogDescription>Usa el nombre de tu empresa como referencia.</DialogDescription>
            </DialogHeader>
            <dl className="grid grid-cols-[7rem_1fr] gap-2 text-sm">
              <dt className="text-muted-foreground">Banco</dt>
              <dd className="font-semibold">{datos.banco}</dd>
              <dt className="text-muted-foreground">Cuenta</dt>
              <dd className="font-mono font-semibold">{datos.cuenta}</dd>
              <dt className="text-muted-foreground">A nombre de</dt>
              <dd className="font-semibold">{datos.titular}</dd>
            </dl>
            <DialogFooter>
              <Button variant="outline" onClick={() => setViendo(false)}>
                Cerrar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </li>
  );
}

function DialogoDePedido({ tipo, onCerrar }: { tipo: PedidoDeFacturacion; onCerrar: () => void }) {
  const empresa = useEmpresaStore((s) => s.empresa);
  const [enviando, setEnviando] = useState(false);
  const enSoporte = useEnSoporte();
  const p = PEDIDOS[tipo];
  const correo = empresa?.billingInfo?.correo || empresa?.email;

  const pedir = async () => {
    setEnviando(true);
    try {
      await miEmpresaService.pedir(tipo);
      toast({
        title: 'Pedido enviado',
        description: correo ? `Te respondemos a ${correo}.` : 'Te respondemos por correo.',
        variant: 'success',
      });
      onCerrar();
    } catch (e: any) {
      toast({
        title: 'No se pudo enviar el pedido',
        description: e?.response?.data?.error ?? e?.message,
        variant: 'destructive',
      });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open onOpenChange={(a) => !a && onCerrar()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{p.titulo}</DialogTitle>
          <DialogDescription>{p.texto}</DialogDescription>
        </DialogHeader>
        <p className="flex items-center gap-2 rounded-xl bg-muted/60 px-3 py-2.5 text-sm">
          <Mail className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          {correo ? (
            <span>
              Te respondemos a <b>{correo}</b>.
            </span>
          ) : (
            <span>
              Completa el correo para facturas (a la derecha) para que sepamos dónde responderte.
            </span>
          )}
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button
            variant="degradado"
            onClick={pedir}
            isLoading={enviando}
            disabled={enviando || enSoporte}
            title={enSoporte ? AVISO_SOPORTE_NO_PUEDE : undefined}
          >
            {p.boton}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** A nombre de quién y a dónde van las facturas. Arranca con los datos de la empresa. */
function DatosDeFacturacionCard() {
  const empresa = useEmpresaStore((s) => s.empresa)!;
  const guardarEmpresa = useEmpresaStore((s) => s.guardar);
  const enSoporte = useEnSoporte();
  const desde = (): Required<Omit<DatosDeFacturacion, 'debitoAutomatico'>> => ({
    razonSocial: empresa.billingInfo?.razonSocial ?? empresa.name ?? '',
    nit: empresa.billingInfo?.nit ?? empresa.nit ?? '',
    correo: empresa.billingInfo?.correo ?? empresa.email ?? '',
    copiaA: empresa.billingInfo?.copiaA ?? '',
    ciudad: empresa.billingInfo?.ciudad ?? '',
    direccion: empresa.billingInfo?.direccion ?? empresa.address ?? '',
  });
  const [datos, setDatos] = useState(desde);
  const [guardando, setGuardando] = useState(false);

  const errores = {
    razonSocial: !datos.razonSocial.trim() ? 'Falta a nombre de quién facturamos.' : null,
    correo: !CORREO.test(datos.correo.trim()) ? 'Falta un correo válido.' : null,
    copiaA:
      datos.copiaA.trim() && !CORREO.test(datos.copiaA.trim()) ? 'No parece un correo.' : null,
  };
  const valido = !Object.values(errores).some(Boolean);

  const guardar = async () => {
    setGuardando(true);
    try {
      const limpio = Object.fromEntries(Object.entries(datos).map(([k, v]) => [k, v.trim()]));
      // Si la empresa todavía no tenía NIT, el de facturación pasa a ser el suyo (Resumen y PDF).
      await guardarEmpresa({
        billingInfo: limpio,
        ...(!empresa.nit && limpio.nit ? { nit: limpio.nit } : {}),
      });
      toast({ title: 'Datos de facturación guardados', variant: 'success' });
    } catch (e: any) {
      toast({
        title: 'No se pudieron guardar',
        description: e?.response?.data?.error ?? e?.message,
        variant: 'destructive',
      });
    } finally {
      setGuardando(false);
    }
  };

  const campo = (
    clave: keyof typeof datos,
    texto: string,
    extra?: { placeholder?: string; tipo?: string }
  ) => {
    const error = (errores as Record<string, string | null>)[clave] ?? null;
    return (
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`factura-${clave}`} className="font-semibold">
          {texto}
        </Label>
        <Input
          id={`factura-${clave}`}
          type={extra?.tipo ?? 'text'}
          value={datos[clave]}
          placeholder={extra?.placeholder}
          aria-invalid={!!error}
          // Soporte sólo mira: lee los datos, no los teclea (guardar ya está frenado).
          readOnly={enSoporte}
          onChange={(e) => setDatos((d) => ({ ...d, [clave]: e.target.value }))}
          className={cn('h-12 rounded-xl text-[15px]', error && 'border-destructive')}
        />
        {error && <p className="text-xs text-destructive">{error}</p>}
      </div>
    );
  };

  return (
    <section
      aria-labelledby="titulo-facturacion"
      className="rounded-tarjeta border bg-card p-6 shadow-card"
    >
      <h2 id="titulo-facturacion" className="text-xl font-semibold">
        Datos de facturación
      </h2>
      <p className="text-sm text-muted-foreground">
        A nombre de quién y a dónde enviamos tus facturas.
      </p>
      <form
        className="mt-5 flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (valido) void guardar();
        }}
      >
        {campo('razonSocial', 'Facturar a nombre de')}
        <div className="grid gap-4 sm:grid-cols-2">
          {campo('nit', 'NIT', { placeholder: 'Ej.: 1234567019' })}
          {campo('correo', 'Correo para facturas', {
            tipo: 'email',
            placeholder: 'facturas@tuempresa.com',
          })}
          {campo('copiaA', 'Copia a (opcional)', {
            tipo: 'email',
            placeholder: 'finanzas@tuempresa.com',
          })}
          {campo('ciudad', 'Ciudad', { placeholder: 'Santa Cruz' })}
        </div>
        {campo('direccion', 'Dirección fiscal')}
        <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">
            {datos.correo && CORREO.test(datos.correo)
              ? `Tus facturas llegan a ${datos.correo}${datos.copiaA && CORREO.test(datos.copiaA) ? ` con copia a ${datos.copiaA}` : ''}.`
              : ''}
          </p>
          <Button
            type="submit"
            variant="degradado"
            size="lg"
            className="px-6"
            disabled={!valido || guardando || enSoporte}
            title={enSoporte ? AVISO_SOPORTE_NO_PUEDE : undefined}
            isLoading={guardando}
          >
            <Save className="size-4" aria-hidden="true" />
            Guardar datos
          </Button>
        </div>
      </form>
    </section>
  );
}
